import { addResponseStatusToSpan } from '../../../utils/httpTracing';
import type { DateTime } from 'effect';
import { Effect, Request, RequestResolver } from 'effect';

import type {
  ApiInfrastructureErrors,
  BackendApiErrorUnion,
} from '../../../data/error/backendApi/_internal/createBackendApiError';
import { createBackendApiError } from '../../../data/error/backendApi/_internal/createBackendApiError';
import { liftRequest } from '../context';
import { TaxonExpandedResponse } from '../../../capability/categoryListing/taxonExpandedSchema';
import { schemaBodyJsonClean } from '@vs-shop/schemas/transform/schemaBodyJsonClean';
import { rawUrlEncode } from '@vs-shop/domain/string/rawUrlEncode';
import { BackendApi } from '../../../service/api/BackendApi';
import type { AuthToken } from '../../../service/auth/AuthToken';
import type { LoggerConfig } from '../../../service/logger/LoggerConfig';
import { ApiModule } from '../../../service/logger/ApiModule';
import { logApiOperation } from '../../../utils/logger/apiLogger';
import type * as HttpClient from 'effect/unstable/http/HttpClient';
import * as HttpClientResponse from 'effect/unstable/http/HttpClientResponse';

const ENDPOINT = '/api/v2/shop/taxons/{code}/expand';
const HTTP_METHOD = 'get';

export const GetTaxonExpandedByCodeError = createBackendApiError({
  domain: 'Category',
  endpoint: 'GetTaxonExpandedByCode',
  path: ENDPOINT,
  identifierField: 'code',
} as const);

export type CategoryGetTaxonExpandedByCodeError = BackendApiErrorUnion<
  typeof GetTaxonExpandedByCodeError
>;

const getTaxonExpandedByCodeFetch = (code: string) =>
  Effect.gen(function* () {
    const apiClient = yield* BackendApi;
    return yield* apiClient
      .path(ENDPOINT)
      .method(HTTP_METHOD)
      .accept('application/ld+json')
      .fetch({ params: { code: rawUrlEncode(code) } });
  }).pipe(
    Effect.withSpan(`http.${HTTP_METHOD} ${ENDPOINT}`, {
      kind: 'client',
      attributes: {
        'http.request.method': HTTP_METHOD,
        'taxon.code': code,
      },
    })
  );

const getTaxonExpandedByCode = (code: string) =>
  getTaxonExpandedByCodeFetch(code).pipe(
    Effect.map((response) => response.response),
    Effect.tap(addResponseStatusToSpan),
    Effect.withSpan('ValidateResponse'),
    Effect.flatMap(
      HttpClientResponse.matchStatus({
        '2xx': (response) => schemaBodyJsonClean(TaxonExpandedResponse)(response),
        404: (response) =>
          Effect.fail(new GetTaxonExpandedByCodeError.NotFoundError({ code, response })),
        orElse: (response) =>
          Effect.fail(new GetTaxonExpandedByCodeError.ApiRequestError({ code, response })),
      })
    ),
    Effect.tap((result) =>
      logApiOperation(
        ApiModule.CategoryGet,
        'Debug',
        `Successfully resolved taxon for code: ${code} -> name: ${String(result.name)}`
      )
    ),
    Effect.withSpan('api.category.get', {
      kind: 'internal',
      attributes: {
        'taxon.code': code,
      },
    })
  );

type GetTaxonExpandedByCodeRequest = {
  readonly _tag: 'GetTaxonExpandedByCodeRequest';
  readonly code: string;
} & Request.Request<
  TaxonExpandedResponse,
  CategoryGetTaxonExpandedByCodeError | ApiInfrastructureErrors,
  BackendApi | AuthToken | HttpClient.HttpClient | LoggerConfig | DateTime.CurrentTimeZone
>;

const GetTaxonExpandedByCodeRequest = Request.tagged<GetTaxonExpandedByCodeRequest>(
  'GetTaxonExpandedByCodeRequest'
);

const GetTaxonExpandedByCodeResolver = RequestResolver.fromEffect(
  liftRequest((request: GetTaxonExpandedByCodeRequest) => getTaxonExpandedByCode(request.code))
);

export const requestTaxonExpandByCode = (code: string) =>
  Effect.request(GetTaxonExpandedByCodeRequest({ code }), GetTaxonExpandedByCodeResolver);
